import { MESSAGE_MENTION_PATH } from "@repo/shared/message-mention-link";
import { Fragment, type Node as ProseMirrorNode, type Schema } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Extension, type JSONContent } from "@tiptap/react";
import { parseFromMarkdown } from "../../conversion";
import {
  createNodeFromJson,
  insertPastedInline,
  replaceWithPastedBlocks,
} from "./markdown-paste-insertion";

const LEGACY_MENTION_ANNOTATION = "[df-mention";
const MENTION_HTML_RE = /data-type=["']mention["']/i;
const STRUCTURAL_HTML_RE = /<(?:ul|ol|li|blockquote|h[1-6]|pre|table)\b/i;

/**
 * Pasted *plain text* that carries mention tokens becomes mention nodes (LAT-901).
 *
 * Copying a chip out of chat normally carries `text/html` too, and that path needs no plugin:
 * the chip renders the DOM contract `MentionNode.parseHTML` reads, so ProseMirror's own parser
 * rebuilds the node. This covers what is left — text pasted from a plain-text surface (a Teams
 * message, a terminal, a model reply copied as source) that still spells the reference as the
 * canonical token, or as the legacy `[df-mention …]` annotation the converter repairs.
 *
 * A single pasted paragraph is spliced in as inline content, so pasting a reference into the
 * middle of a sentence does not break the sentence into two blocks.
 */
export const MentionTokenPaste = Extension.create({
  name: "mentionTokenPaste",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("mentionTokenPaste"),
        props: {
          handlePaste(view, event) {
            if (!view.state.schema.nodes.mention) return false;

            const text = event.clipboardData?.getData("text/plain") ?? "";
            const html = event.clipboardData?.getData("text/html") ?? "";
            if (!shouldParseMentionTokens(text, html)) return false;

            const blocks = parseFromMarkdown(text);
            if (blocks.length === 0) return false;

            const inlineContent = blocks.length === 1 ? readParagraphInline(blocks[0]) : null;
            const nodes = toProseMirrorNodes(view.state.schema, inlineContent ?? blocks);
            if (!nodes) return false;

            const content = Fragment.fromArray(nodes);
            const inserted = inlineContent
              ? insertPastedInline(view, content)
              : replaceWithPastedBlocks(view, content);
            if (!inserted) return false;

            event.preventDefault();
            return true;
          },
        },
      }),
    ];
  },
});

function shouldParseMentionTokens(text: string, html: string): boolean {
  if (!text.trim()) return false;
  if (!text.includes(MESSAGE_MENTION_PATH) && !text.includes(LEGACY_MENTION_ANNOTATION)) {
    return false;
  }

  // Anything the DOM parser can do better is left to it: chip markup already parses into
  // mention nodes, and structural HTML carries list/heading shape this text cannot.
  return !(html && (MENTION_HTML_RE.test(html) || STRUCTURAL_HTML_RE.test(html)));
}

function readParagraphInline(block: JSONContent | undefined): JSONContent[] | null {
  if (!block || block.type !== "paragraph") return null;
  return block.content && block.content.length > 0 ? block.content : null;
}

/** All-or-nothing: a node this schema cannot hold means the native paste is the better answer. */
function toProseMirrorNodes(schema: Schema, nodes: JSONContent[]): ProseMirrorNode[] | null {
  const converted: ProseMirrorNode[] = [];
  for (const node of nodes) {
    const pmNode = createNodeFromJson(schema, node);
    if (!pmNode) return null;
    converted.push(pmNode);
  }
  return converted;
}
